import { useState } from 'react';

export interface GraphUser {
  id: string;
  displayName: string;
  givenName?: string;
  surname?: string;
  mail?: string;
  userPrincipalName: string;
  jobTitle?: string;
  department?: string;
  mobilePhone?: string;
  businessPhones?: string[];
  employeeId?: string;
  employeeType?: string;
  officeLocation?: string;
  manager?: {
    id: string;
    displayName: string;
    mail?: string;
  };
  groups?: string[];
}

export interface EmployeeUpdateFormData {
  // Step 0: User Selection
  selectedUserId: string;
  selectedUserName: string;
  originalUserData: GraphUser | null;

  // Step 1: Employee Details
  jobTitle: string;
  department: string;
  mobilePhone: string;
  businessPhone: string;
  employeeId: string;
  employmentType: 'Employee' | 'Contractor';
  managerId: string;
  managerName: string;
  managerEmail: string;
  siteCode: string;

  // Step 2: Access
  originalGroups: string[];
  selectedGroups: string[];

  // Step 3: Software
  requestedSoftware: string[];
}

export interface DetectedChanges {
  hasChanges: boolean;
  changes: Partial<EmployeeUpdateFormData>;
  groupsToAdd: string[];
  groupsToRemove: string[];
}

const initialFormData: EmployeeUpdateFormData = {
  selectedUserId: '',
  selectedUserName: '',
  originalUserData: null,
  jobTitle: '',
  department: '',
  mobilePhone: '',
  businessPhone: '',
  employeeId: '',
  employmentType: 'Employee',
  managerId: '',
  managerName: '',
  managerEmail: '',
  siteCode: '',
  originalGroups: [],
  selectedGroups: [],
  requestedSoftware: [],
};

export function useEmployeeUpdateForm() {
  const [formData, setFormData] = useState<EmployeeUpdateFormData>(initialFormData);
  const [currentStep, setCurrentStep] = useState(0);

  // Update form data
  const updateFormData = (updates: Partial<EmployeeUpdateFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Set user data from API
  const setUserData = (user: GraphUser) => {
    setFormData({
      ...initialFormData,
      selectedUserId: user.id,
      selectedUserName: user.displayName,
      originalUserData: user,
      // Pre-populate fields
      jobTitle: user.jobTitle || '',
      department: user.department || '',
      mobilePhone: user.mobilePhone || '',
      businessPhone: user.businessPhones?.[0] || '',
      employeeId: user.employeeId || '',
      employmentType: (user.employeeType as 'Employee' | 'Contractor') || 'Employee',
      managerId: user.manager?.id || '',
      managerName: user.manager?.displayName || '',
      managerEmail: user.manager?.mail || '',
      siteCode: user.officeLocation || '',
      originalGroups: user.groups || [],
      selectedGroups: user.groups || [],
      requestedSoftware: [],
    });
  };

  // Detect changes
  const detectChanges = (): DetectedChanges => {
    if (!formData.originalUserData) {
      return {
        hasChanges: false,
        changes: {},
        groupsToAdd: [],
        groupsToRemove: [],
      };
    }

    const changes: Partial<EmployeeUpdateFormData> = {};
    const original = formData.originalUserData;

    // Compare employee details
    if (formData.jobTitle !== (original.jobTitle || '')) {
      changes.jobTitle = formData.jobTitle;
    }
    if (formData.department !== (original.department || '')) {
      changes.department = formData.department;
    }
    if (formData.mobilePhone !== (original.mobilePhone || '')) {
      changes.mobilePhone = formData.mobilePhone;
    }
    if (formData.businessPhone !== (original.businessPhones?.[0] || '')) {
      changes.businessPhone = formData.businessPhone;
    }
    if (formData.employeeId !== (original.employeeId || '')) {
      changes.employeeId = formData.employeeId;
    }
    if (formData.employmentType !== (original.employeeType || 'Employee')) {
      changes.employmentType = formData.employmentType;
    }
    if (formData.managerId !== (original.manager?.id || '')) {
      changes.managerId = formData.managerId;
      changes.managerName = formData.managerName;
      changes.managerEmail = formData.managerEmail;
    }
    if (formData.siteCode !== (original.officeLocation || '')) {
      changes.siteCode = formData.siteCode;
    }

    // Calculate group changes
    const groupsToAdd = formData.selectedGroups.filter(
      (g) => !formData.originalGroups.includes(g)
    );
    const groupsToRemove = formData.originalGroups.filter(
      (g) => !formData.selectedGroups.includes(g)
    );

    const hasChanges =
      Object.keys(changes).length > 0 ||
      groupsToAdd.length > 0 ||
      groupsToRemove.length > 0 ||
      formData.requestedSoftware.length > 0;

    return {
      hasChanges,
      changes,
      groupsToAdd,
      groupsToRemove,
    };
  };

  // Validate step
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // User selection
        return !!formData.selectedUserId && !!formData.originalUserData;

      case 1: // Employee details
        // Manager is required
        return !!formData.managerId;

      case 2: // Access
        return true; // No validation needed

      case 3: // Software
        return true; // No validation needed

      case 4: // Review
        return detectChanges().hasChanges;

      default:
        return true;
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData(initialFormData);
    setCurrentStep(0);
  };

  // Navigation
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step <= 4) {
      setCurrentStep(step);
    }
  };

  return {
    formData,
    currentStep,
    updateFormData,
    setUserData,
    detectChanges,
    validateStep,
    resetForm,
    nextStep,
    prevStep,
    goToStep,
  };
}
